/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { warning } from '../internal/warning.js';
var warningCache = new Map();

/**
 * Wraps a prop-type validator with a deprecation warning.
 *
 * @param propType - The original prop validator function.
 * @param message - Deprecation message.
 * @returns A new validator function that emits a warning the first time a
 *          deprecated prop is used.
 */
var deprecate = function deprecate(propType, message) {
  var checker = function checker(props, propName, componentName) {
    if (typeof props[propName] === 'undefined') {
      return;
    }
    if (!warningCache.has(componentName)) {
      warningCache.set(componentName, new Set());
    }
    var warnedProps = warningCache.get(componentName);
    if (warnedProps && !warnedProps.has(propName)) {
      warnedProps.add(propName);
      process.env.NODE_ENV !== "production" ? warning(false, message || "The prop `".concat(propName, "` has been deprecated for the ") + "".concat(componentName, " component. It will be removed in the next major ") + "release") : void 0;
    }
    for (var _len = arguments.length, rest = new Array(_len > 3 ? _len - 3 : 0), _key = 3; _key < _len; _key++) {
      rest[_key - 3] = arguments[_key];
    }
    return propType.apply(void 0, [props, propName, componentName].concat(rest));
  };
  return checker;
};
export { deprecate };